<template>
    <div class="w-100">
        <SectionTitle title="Turn Scatters">
            <MoreInfo :id="'turn-scatters-info-modal'" :title="'Turn Scatters Description'">
                <p>The turn scatter plots display entry speed and turn angle for each significant turn from the selected active sessions, 
                    benchmarked against both historical match data and/or current rehab sessions.</p>
            </MoreInfo>
        </SectionTitle>
        <MissingDataText v-if="!hasData" :message="'Turn Scatters Unavailable'" />
        <div v-else>
            <div class="p-0">
                <div>
                    <div>
                        <div class="scatter-legends">
                            <div class="scatter-legend" id="match-profile"></div>
                            <span class="ml-1 mr-3">Match Profile</span> <br />
                            <div class="scatter-legend" id="rehab"></div>
                            <span class="ml-1 mr-3">Rehab</span> <br />
                            <div class="scatter-legend" id="active-sessions"></div>
                            <span class="ml-1 mr-3">Active Sessions</span> <br />
                        </div>
                    </div>
                    <div class="row">
                        <span class="float-left mb-2">
                            Left Side Turns
                        </span>
                    </div>
                    <div class="row">
                        <TurnScattersChart id="left-chart" class="col" style="height: 28vh"
                            :turns="this.scatterTurnsLeft" :minSpeed="this.minSpeed" :isRehabSession="true"
                            :additionalTooltipText="this.turnsScattersAdditionalTooltipText"
                            :rehabDateString="firstRehabDate" :highlightedDates="highlightedDates" :maxSpeed="this.maxSpeed" :displayYAxis="true"
                            :maintainAspectRatio="false" :minAngle="this.minAngle" :maxAngle="this.maxAngle" :rangeManuallySelected="rangeManuallySelected"/>
                    </div>
                    <div class="row">
                        <span class="float-left mb-2">
                            Right Side Turns
                        </span>
                    </div>
                    <div class="row">
                        <TurnScattersChart id="right-chart" class="col" style="height: 28vh"
                            :turns="this.scatterTurnsRight" :minSpeed="this.minSpeed" :isRehabSession="true"
                            :additionalTooltipText="this.turnsScattersAdditionalTooltipText"
                            :rehabDateString="firstRehabDate" :highlightedDates="highlightedDates" :maxSpeed="this.maxSpeed" :displayYAxis="true"
                            :maintainAspectRatio="false" :minAngle="this.minAngle" :maxAngle="this.maxAngle" :rangeManuallySelected="rangeManuallySelected" />
                    </div>
                    <div class="row">
                        <div class="col"></div>
                        <p class="text-center pt-0 mt-0 col2" style="font-size: 12px; height: 5vh;">Entry
                            speed (<MeasurementUnit :type="Speed" />)
                        </p>
                        <div class="col"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { UserData } from "@/components/UserData";
import { getTurnEntrySpeedClassifications } from "@/utils/TurnEntrySpeedBoundaryHelpers";
import { MeasurementTypes, getMeasurementUnit, MeasurementUnit as MU } from "@/utils/MeasurementSystem";
import turnFiltersStore from "./turnFiltersStore";
import { getFilteredTurns } from "@/utils/TurnScattersHelpers";

export default {
    props: {
        data: Array,
        firstRehabDate: String,
        highlightedDates: Object,
        rangeManuallySelected: Boolean
    },
    components: {
    },
    data() {
        return {
            hasData: false,
            turnEntrySpeedBoundaries: UserData.getTurnEntrySpeedBoundaries(),
            formatStandard: MU.prototype.formatStandard,
            formatFine: MU.prototype.formatFine,
            Speed: MeasurementTypes.Speed,
            speedUnit: getMeasurementUnit(MeasurementTypes.Speed),
            scatterTurnsLeft: [],
            scatterTurnsRight: [],
            minSpeed: 1,
            maxSpeed: null,
            minAngle: 0,
            maxAngle: 180,
        };
    },
    methods: {
        getTurnScatters() {
            this.hasData = this.data.length > 0;
            if (this.hasData) {
                const results = getFilteredTurns(
                    this.data, this.filterModel, this.speedOptions,
                    (sessionDate, seasonSelected) => seasonSelected.contains(sessionDate),
                    this.speedUnit
                );
                const { leftTurns, rightTurns, minAngle, maxAngle, minSpeed, maxSpeed } = results;
                this.scatterTurnsLeft = leftTurns;
                this.scatterTurnsRight = rightTurns;
                this.minAngle = minAngle;
                this.maxAngle = maxAngle;
                this.minSpeed = minSpeed;
                this.maxSpeed = maxSpeed;
            }
        },
        turnsScattersAdditionalTooltipText(tooltipItem, data) {
            const indexData = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            return ['Date: ' + indexData.date];
        },
    },
    computed: {
        speedOptions() {
            const turnEntrySpeedClassifications = getTurnEntrySpeedClassifications(this.turnEntrySpeedBoundaries, this.speedUnit);
            for (let i = 0; i < turnEntrySpeedClassifications.length; i++) {
                turnEntrySpeedClassifications[i].value = i;
            }
            return turnEntrySpeedClassifications;
        },
        filters() {
            return turnFiltersStore.state;
        },
        filterModel() {
            return turnFiltersStore.state;
        },
        updateKey() {
            return turnFiltersStore.state.updateKey;
        }
    },
    watch: {
        updateKey: {
            handler() {
                this.getTurnScatters();
            },
            immediate: true
        }, 
        data: {
            handler() {
                this.getTurnScatters();
            },
            immediate: true
        }, 
    },
    async mounted() {
        turnFiltersStore.commit("RESET_FILTERS"); //Reset the filters when user navigates away from page or new player is selected
        this.getTurnScatters();
    },
};
</script>

<style scoped>
.scatter-legends{
  width: 22vw;
  display: flex;
  justify-content: space-evenly;
  margin: auto;
  font-size: 12px;
  align-items: center;
}
.scatter-legend {
  width: 1.4vw;
  height: 1.4vh;
}
#rehab{
  background-color: var(--sportlight-teal);

}
#match-profile{
  background-color: var(--bg-secondary-dark);
}
#active-sessions{
  background-color: var(--orange);
}

</style>